import { useCallback, useMemo } from "react";
import type { RefObject } from "react";
import { EmptyState, Skeleton, SkeletonText } from "@astrum/ui";
import { groupMessages } from "../../lib/chat-format.js";
import { MessageRun } from "./MessageRun.js";
import type { PendingMessage } from "./types.js";

interface MessageListProps {
  /** The scroll container, shared with useChannelMessages so it can hold the reader's place. */
  scrollRef: RefObject<HTMLDivElement | null>;
  messages: PendingMessage[];
  loading: boolean;
  loadingOlder: boolean;
  onLoadOlder: () => void;
}

/**
 * The scrolling thread: day separators, author runs, and the older-history
 * trigger near the top. Each row's actions come from MessageActionsContext.
 *
 * Not virtualised: a channel holds the pages scrolled back through, 50 at a
 * time, and runs collapse consecutive messages, so the DOM stays modest.
 */
export function MessageList({ scrollRef, messages, loading, loadingOlder, onLoadOlder }: MessageListProps) {
  const dayGroups = useMemo(() => groupMessages(messages), [messages]);

  /** Near the top of the thread, with more history available: fetch it. */
  const handleScroll = useCallback(() => {
    const node = scrollRef.current;
    if (!node) return;
    if (node.scrollTop < 200) onLoadOlder();
  }, [scrollRef, onLoadOlder]);

  return (
    // role="log" is a polite live region, so a screen reader announces new
    // messages as they arrive; focusable so the scroller is keyboard-reachable.
    <div
      className="chat-thread__scroll"
      ref={scrollRef}
      onScroll={handleScroll}
      role="log"
      aria-label="Messages"
      aria-relevant="additions"
      tabIndex={0}
    >
      {loadingOlder && (
        <div className="chat-thread__loading-older">
          <Skeleton circle width={20} height={20} />
          <span>Loading earlier messages…</span>
        </div>
      )}
      {loading ? (
        <div className="chat-thread__loading">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="chat-run">
              <Skeleton circle width={32} height={32} />
              <div className="chat-run__body">
                <SkeletonText lines={2} />
              </div>
            </div>
          ))}
        </div>
      ) : dayGroups.length === 0 ? (
        <EmptyState icon="chat" title="No messages yet" description="Say something to get the conversation going." />
      ) : (
        dayGroups.map((day) => (
          <div key={day.key}>
            <div className="chat-day-separator">
              <span>{day.label}</span>
            </div>
            {day.runs.map((run, i) =>
              run.system ? (
                <div key={run.messages[0]!.id} id={`message-${run.messages[0]!.id}`} className="chat-system">
                  {run.messages[0]!.body}
                </div>
              ) : (
                <MessageRun key={`${run.authorId}-${i}`} authorId={run.authorId} authorName={run.authorName} messages={run.messages} />
              ),
            )}
          </div>
        ))
      )}
    </div>
  );
}
